import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Trash2, Eye, UserCog, Pencil, X, Loader2, Save } from 'lucide-react';
import { RowActionsMenu } from './RowActionsMenu';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '../ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';

export function AdminUsersTab({ 
  users, 
  searchQuery, 
  handleDeleteUser,
  handleImpersonateUser,
  handleToggleBookingManager,
  refreshUsers
}) {
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openEditDialog = (user) => {
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      is_booking_manager: user.is_booking_manager || false,
      is_artist_manager: user.is_artist_manager || false,
      is_club_owner: user.is_club_owner || false,
      is_event_manager: user.is_event_manager || false,
      notes: user.admin_notes || ''
    });
    setEditingUser(user);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${editingUser.user_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success('User updated successfully');
        setEditingUser(null);
        if (refreshUsers) refreshUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">User</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Clubs</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Permissions</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Joined</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-slate-900">{u.name}</p>
                    <p className="text-sm text-slate-500">{u.email}</p>
                    <p className="text-xs text-slate-400 font-mono">ID: {u.user_id}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Badge variant="secondary">{u.club_count || 0} clubs</Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {u.is_admin && (
                      <Badge className="bg-slate-900 text-white">Admin</Badge>
                    )}
                    {u.is_booking_manager && (
                      <Badge className="bg-violet-600 text-white">Booking Mgr</Badge>
                    )}
                    {u.is_artist_manager && (
                      <Badge className="bg-emerald-600 text-white">Artist Mgr</Badge>
                    )}
                    {u.is_club_owner && (
                      <Badge className="bg-blue-600 text-white">Club Owner</Badge>
                    )}
                    {u.is_event_manager && (
                      <Badge className="bg-teal-600 text-white">Event Mgr</Badge>
                    )}
                    {!u.is_admin && !u.is_booking_manager && !u.is_artist_manager && !u.is_club_owner && !u.is_event_manager && (
                      <Badge variant="outline">User</Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    const items = [
                      {
                        label: 'Edit User',
                        icon: <Pencil className="w-4 h-4 text-violet-600" />,
                        onClick: () => openEditDialog(u),
                        testId: `edit-user-${u.user_id}`,
                      },
                      {
                        label: 'View as This User',
                        icon: <Eye className="w-4 h-4 text-blue-600" />,
                        onClick: () => handleImpersonateUser(u),
                        testId: `impersonate-user-${u.user_id}`,
                      },
                    ];
                    if (!u.is_admin) {
                      items.push({
                        label: u.is_booking_manager ? 'Remove Booking Manager' : 'Make Booking Manager',
                        icon: <UserCog className="w-4 h-4 text-violet-600" />,
                        onClick: () => handleToggleBookingManager(u.user_id, !u.is_booking_manager),
                        testId: `toggle-booking-manager-${u.user_id}`,
                      });
                    }
                    const groups = [{ items }];
                    if (!u.is_admin) {
                      groups.push({
                        items: [{
                          label: 'Delete User',
                          icon: <Trash2 className="w-4 h-4" />,
                          destructive: true,
                          onClick: () => handleDeleteUser(u.user_id),
                          testId: `delete-user-${u.user_id}`,
                          confirm: {
                            title: 'Delete User',
                            description: `Are you sure you want to delete "${u.name}"? This will also delete all their clubs, reviews, and data.`,
                            actionLabel: 'Delete',
                          },
                        }],
                      });
                    }
                    return <RowActionsMenu groups={groups} triggerTestId={`manage-user-${u.user_id}`} />;
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {filteredUsers.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No users found
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and permissions
            </DialogDescription>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="User name"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="user@example.com"
                />
              </div>

              <div className="space-y-3">
                <Label>Permissions</Label>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_booking_manager"
                    checked={editForm.is_booking_manager}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_booking_manager: checked }))}
                  />
                  <label htmlFor="is_booking_manager" className="text-sm text-slate-700">
                    Booking Manager
                    <span className="text-xs text-slate-500 block">Can manage weekend event bookings</span>
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_artist_manager"
                    checked={editForm.is_artist_manager}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_artist_manager: checked }))}
                  />
                  <label htmlFor="is_artist_manager" className="text-sm text-slate-700">
                    Artist Manager
                    <span className="text-xs text-slate-500 block">Can add and manage artist profiles</span>
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_club_owner"
                    checked={editForm.is_club_owner}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_club_owner: checked }))}
                  />
                  <label htmlFor="is_club_owner" className="text-sm text-slate-700">
                    Club Owner
                    <span className="text-xs text-slate-500 block">Can create and manage clubs</span>
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_event_manager"
                    checked={editForm.is_event_manager}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_event_manager: checked }))}
                  />
                  <label htmlFor="is_event_manager" className="text-sm text-slate-700">
                    Event Manager
                    <span className="text-xs text-slate-500 block">Can check people in at events using QR scanner</span>
                  </label>
                </div>
              </div>

              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Internal notes about this user..."
                  className="h-20"
                />
              </div>

              {editingUser.is_admin && (
                <div className="bg-slate-100 rounded-lg p-3 text-sm text-slate-600">
                  <strong>Note:</strong> This user is an Admin. Admin privileges cannot be changed here.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveUser} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
